import CustomButton from "components/button/CustomButton";
import useFormatPrice from "hooks/useFormatPrice";
import useLanguages from "hooks/useLanguages";
import { FormattedMessage, useIntl } from "react-intl";
import { OrderType, PaymentInfo, RefundInfo } from "types/orderType";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PaymentService from "services/paymentService";
import { PaymentMethod } from "const/paymentMethod";
import CardLayout from "components/layout/CardLayout";
import clsx from "clsx";
import { providerClasses } from "utils/tailwindUtils";
import { useNavigate } from "react-router-dom";

interface PaymentRefundInfoSectionProps {
  order: OrderType;
  type: "payment" | "refund";
  boxShadow?: boolean;
}

const PaymentRefundInfoSection = ({
  order,
  type,
  boxShadow = true,
}: PaymentRefundInfoSectionProps) => {
  const { isKorean } = useLanguages();
  const navigate = useNavigate();
  const formatPrice = useFormatPrice();

  const paymentInfo = order.paymentInfo;
  const refundInfo = order.refundInfo;

  const isPaidByCard =
    paymentInfo?.paymentMethod === PaymentMethod.CREDIT ||
    paymentInfo?.paymentMethod === PaymentMethod.CORPORATE;
  const isPaidByHanpass =
    order.paymentInfo?.paymentMethod === PaymentMethod.HANPASS;

  const navigateToReceiptPage = async () => {
    try {
      if (order.paymentInfo?.paymentId) {
        const response = await PaymentService.getReceiptUrl(
          order.paymentInfo?.paymentId
        );
        // window.location.href = response;
        window.open(response, "_blank");
      }
    } catch {
      alert("잠시 후 다시 시도해주세요.");
    }
  };

  const navigateToHanpassReceiptPage = () => {
    // console.log(`${location.pathname}/receipt`);
    navigate(`receipt/${paymentInfo?.paymentId}`);
  };

  const getHeader = () => {
    return (
      <div className="w-full space-y-1">
        <div className="flex w-full justify-between">
          <p className="font-bold">
            <FormattedMessage
              id={
                type === "payment" ? "orders.paymentTitle" : "orders.cancelInfo"
              }
            />
          </p>
          {/* 영수증 출력 */}
          {(isPaidByCard || isPaidByHanpass) && (
            <CustomButton
              onClick={
                isPaidByCard
                  ? navigateToReceiptPage
                  : navigateToHanpassReceiptPage
              }
              size="small"
              variant="text"
              fullWidth={false}
              // disabled={isLoading}
              style={{
                padding: 0,
                minHeight: "24px",
              }}
            >
              <p className="text-sm font-bold">
                {isKorean ? "영수증 출력" : "Print receipt"}
              </p>

              <NavigateNextIcon fontSize="small" />
            </CustomButton>
          )}
        </div>
        <div className="flex w-full justify-between">
          {/* 총 결제 금액 */}
          <p>
            <FormattedMessage
              id={
                type === "payment"
                  ? "orders.totalPaymentAmount"
                  : "orders.cancelAmount"
              }
            />
            :
          </p>
          <p
            className={clsx(
              "font-bold",
              type === "payment"
                ? providerClasses["text-primary"]
                : "text-error"
            )}
          >
             {formatPrice(type === "payment" ? order.totalPrice : order.totalCancelAmount)}
          </p>
        </div>
      </div>
    );
  };

  return (
    (paymentInfo || refundInfo) && (
      <CardLayout
        header={getHeader()}
        className={clsx("w-full", boxShadow ? "" : "shadow-none")}
      >
        <div className="w-full">
          <p className="font-medium">{isKorean ? "결제 수단" : "Payment method"}</p>
        </div>
        {type === "payment" ? (
          <PaymentRefundDetail paymentRefundInfo={paymentInfo} />
        ) : (
          <PaymentRefundDetail paymentRefundInfo={refundInfo} />
          // 여기에 Refundinfo가 아니고 paymentInfo를 넣어야하는건가!??
        )}
        {/* 라차 적립금 */}
        {/* {Boolean(order.pointUsed) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography variant="body1">
            {intl.formatMessage({
              id: type === "payment" ? "orders.usedPoint" : "point.refundPoint",
            })}
            :
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            {formatPrice(order.pointUsed)}
          </Typography>
        </Stack>
      )} */}
        {/* 한패스 쿠폰 */}
        {Boolean(order.externalCouponDiscount) && (
          <div className="flex w-full justify-between">
            <p>{isKorean ? "한패스 쿠폰" : "Hanpass coupon"}:</p>
            <p className="font-bold">
              {formatPrice(order.externalCouponDiscount)}
            </p>
          </div>
        )}
      </CardLayout>
    )
  );
};

export default PaymentRefundInfoSection;

interface PaymentRefundDetailProps {
  paymentRefundInfo: PaymentInfo | RefundInfo;
}

const PaymentRefundDetail = ({
  paymentRefundInfo,
}: PaymentRefundDetailProps) => {
  const intl = useIntl();
  const { isKorean } = useLanguages();
  const formatPrice = useFormatPrice();

  const getCategoryTitle = (
    paymentMethod?: PaymentInfo["paymentMethod"] | RefundInfo["paymentMethod"]
  ) => {
    switch (paymentMethod) {
      case "Credit":
      case "Overseas":
        return intl.formatMessage({ id: "payment.creditCard" });
      case "POINT":
        return isKorean ? "라차 포인트" : "Lacha Point";
      case "KeyIn":
        return isKorean ? "법인카드" : "Corporate Card";
      case "Hanpass":
        return isKorean ? "한패스월렛" : "Hanpass Wallet";
      case "GME":
        return isKorean ? "GME 페이" : "GME Pay";
      default:
        return intl.formatMessage({ id: "payment.creditCard" });
      // const _exhaustiveCheck: never = paymentMethod;
      // return _exhaustiveCheck;
    }
  };

  return (
    <div className="w-full">
      {Boolean(paymentRefundInfo?.paymentAmount) && (
        <div className="flex w-full justify-between">
          <p>{getCategoryTitle(paymentRefundInfo?.paymentMethod)}:</p>
          <p className="font-bold">
            {formatPrice(paymentRefundInfo?.paymentAmount)}
          </p>
        </div>
      )}
      {Boolean(paymentRefundInfo?.mileageAmount) && (
        <div className="flex w-full justify-between">
          <p>{isKorean ? "라차 포인트" : "Lacha Point"}</p>
          <p className="font-bold">
            {formatPrice(paymentRefundInfo?.mileageAmount)}
          </p>
        </div>
      )}
    </div>
  );
};
